import { FC } from 'react';
import {
  WellsOverviewSection as SharedWellsOverviewSection,
  type WellsOverviewConfig,
} from 'pages/documents/structuring/wells-overview-section';
import type { StagedWell } from './types';
import {
  fetchWellOverviewAtom,
  removeWellAtom,
  restoreWellAtom,
  wellDetailUrl,
  wellOverviewDataAtom,
  wellOverviewErrorAtom,
  wellOverviewUrl,
} from './atoms/wells';
import { selectedWellIndexAtom } from './atoms/layout';
import { AddWellPanel } from './add-well-panel';
import { WellEditPanel } from './well-edit-panel';

// AFE Well Overview — a thin doctype wrapper over the shared section
// (pages/documents/structuring/wells-overview-section). All behavior lives in
// the shared component; this file supplies the AFE wiring: the AFE wells atom
// bundle, the module-level `selectedWellIndexAtom` singleton, the AFE staged
// meta fields, and the AFE Add/Edit panels. AFE deletes on the single trash
// click (no confirm modal) and has no companion overview to reload.

const AFE_WELLS_OVERVIEW_CONFIG: WellsOverviewConfig<StagedWell> = {
  wellOverviewDataAtom,
  wellOverviewErrorAtom,
  fetchWellOverviewAtom,
  removeWellAtom,
  restoreWellAtom,
  wellOverviewUrl,
  wellDetailUrl,
  // AFE's drill-in selection is a module-level singleton (ignores documentId).
  getSelectedWellIndexAtom: () => selectedWellIndexAtom,
  // UWI / County-State / Formation always render; the rest only when present;
  // Legal Description spans the full cell width.
  stagedOverviewFields: (staged) => [
    { label: 'UWI / API Number', value: staged.uwiApi ?? '—' },
    { label: 'County/State', value: staged.countyState ?? '—' },
    { label: 'Formation', value: staged.formation ?? '—' },
    ...(staged.basin ? [{ label: 'Basin', value: staged.basin }] : []),
    ...(staged.tvd ? [{ label: 'TVD', value: staged.tvd }] : []),
    ...(staged.tmd ? [{ label: 'TMD', value: staged.tmd }] : []),
    ...(staged.shl ? [{ label: 'SHL', value: staged.shl }] : []),
    ...(staged.bhl ? [{ label: 'BHL', value: staged.bhl }] : []),
    ...(staged.ftp ? [{ label: 'FTP', value: staged.ftp }] : []),
    ...(staged.ltp ? [{ label: 'LTP', value: staged.ltp }] : []),
    ...(staged.quarterSubdivision
      ? [{ label: 'Quarter / Subdivision', value: staged.quarterSubdivision }]
      : []),
    ...(staged.prospect ? [{ label: 'Prospect', value: staged.prospect }] : []),
    ...(staged.abstractSurvey
      ? [{ label: 'Abstract / Survey', value: staged.abstractSurvey }]
      : []),
    ...(staged.legalDescription
      ? [
          {
            label: 'Legal Description',
            value: staged.legalDescription,
            fullWidth: true,
          },
        ]
      : []),
  ],
  // AFE has no companion overview to refresh on a well mutation.
  useExtraReload: () => () => {},
  AddWellPanel,
  WellEditPanel,
};

interface Props {
  documentId: number | string;
}

export const WellsOverviewSection: FC<Props> = ({ documentId }) => (
  <SharedWellsOverviewSection<StagedWell>
    documentId={documentId}
    config={AFE_WELLS_OVERVIEW_CONFIG}
  />
);
